import { Injectable, inject } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { APP_CONFIG_TOKEN } from '../config';
import { AuthService } from './auth.service';

import { logger } from '../../helpers/logger';
@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private angularPlugin: AngularPlugin;
  private readonly config = inject(APP_CONFIG_TOKEN);
  private readonly authService = inject(AuthService);
  private isInitialized = false;

  constructor(private router: Router) {
    this.angularPlugin = new AngularPlugin();
  }

  private initialize(): void {
    const connectionString = this.config.applicationInsightsConnectionString;

    if (!connectionString || connectionString.trim() === '' || connectionString.includes('#{')) {
      logger.warn('Application Insights: No connection string provided. Telemetry will not be collected.');
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        extensions: [this.angularPlugin],
        extensionConfig: {
          [this.angularPlugin.identifier]: { router: this.router },
        },
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        // Dont add the 'request-id' headers to Cesium Ion requests
        correlationHeaderExcludedDomains: ['cesium.com', 'ion.cesium.com'],
      },
    });

    this.appInsights.loadAppInsights();

    //Completely skip tracking dependencies to Cesium Ion
    this.appInsights.addDependencyInitializer(details => {
      if (details.item.target?.includes('cesium.com') || details.item.target?.includes('ion.cesium.com')) {
        return false;
      }
      return true;
    });

    this.appInsights.trackPageView();
    this.isInitialized = true;
    logger.debug('Application Insights initialized successfully');
  }

  private ensureInitialized(): boolean {
    if (!this.isInitialized) {
      // Try to initialize now if we haven't yet
      this.initialize();
    }
    return this.isInitialized && this.appInsights !== null;
  }

  trackEvent(name: string, properties?: { [key: string]: any }, measurements?: { [key: string]: number }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.trackEvent({ name, properties, measurements });
  }

  trackException(exception: Error, properties?: { [key: string]: any }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.trackException({ exception, properties });
  }

  trackTrace(message: string, properties?: { [key: string]: any }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.trackTrace({ message, properties });
  }

  trackMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.trackMetric({ name, average, properties });
  }

  trackPageView(name?: string, uri?: string, properties?: { [key: string]: any }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.trackPageView({ name, uri, properties });
  }

  trackDependencyData(
    id: string,
    target: string,
    name: string,
    data: string,
    duration: number,
    success: boolean,
    resultCode?: number
  ): void {
    if (!this.ensureInitialized()) return;

    const dependencyData: any = {
      id,
      target,
      name,
      data,
      duration,
      success,
    };

    if (resultCode !== undefined) {
      dependencyData.responseCode = resultCode;
    }

    this.appInsights!.trackDependencyData(dependencyData);
  }

  startTrackPage(name?: string): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.startTrackPage(name);
  }

  stopTrackPage(name?: string, url?: string, properties?: { [key: string]: any }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.stopTrackPage(name, url, properties);
  }

  startTrackEvent(name: string): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.startTrackEvent(name);
  }

  stopTrackEvent(name: string, properties?: { [key: string]: any }, measurements?: { [key: string]: number }): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.stopTrackEvent(name, properties, measurements);
  }

  flush(): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.flush();
  }

  setUserId(userId: string): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.setAuthenticatedUserContext(userId);
  }

  clearUserId(): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.clearAuthenticatedUserContext();
  }

  addTelemetryInitializer(telemetryInitializer: (envelope: any) => boolean | void): void {
    if (!this.ensureInitialized()) return;
    this.appInsights!.addTelemetryInitializer(telemetryInitializer);
  }

  trackUserAction(action: string, details?: string, properties?: { [key: string]: any }): void {
    const eventProperties = {
      action,
      details,
      timestamp: new Date().toISOString(),
      ...properties,
    };
    this.trackEvent('UserAction', eventProperties);
  }

  trackPerformance(
    operationName: string,
    duration: number,
    success: boolean,
    properties?: { [key: string]: any }
  ): void {
    const eventProperties = {
      operationName,
      success: success.toString(),
      timestamp: new Date().toISOString(),
      ...properties,
    };
    const measurements = {
      duration,
    };
    this.trackEvent('Performance', eventProperties, measurements);
  }

  trackApiCall(url: string, method: string, duration: number, statusCode: number, success: boolean): void {
    this.trackDependencyData(`${method} ${url}`, url, `${method} ${url}`, '', duration, success, statusCode);
  }

  private extractUserContext(): Record<string, string> {
    const userContext: Record<string, string> = {};

    try {
      // Get current user synchronously (if available)
      let currentUser: any = null;
      this.authService.currentUser
        .pipe()
        .subscribe(user => {
          currentUser = user;
        })
        .unsubscribe();

      if (currentUser) {
        // Include user email as primary identifier
        if (currentUser.email) {
          userContext['userEmail'] = currentUser.email;
          userContext['userId'] = currentUser.email; // For consistency with server-side
        }

        // Include user name
        if (currentUser.firstName || currentUser.lastName) {
          const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
          if (fullName) userContext['userName'] = fullName;
        }

        // Include database user ID
        if (currentUser.id) {
          userContext['databaseUserId'] = currentUser.id;
        }

        // Include client context
        if (currentUser.clientId) {
          userContext['clientId'] = currentUser.clientId.toString();
        }

        // Include admin status
        if (currentUser.isAdmin !== undefined) {
          userContext['isAdmin'] = currentUser.isAdmin.toString();
        }

        // Include role information
        if (currentUser.globalRole) {
          userContext['globalRole'] = currentUser.globalRole;
        }

        userContext['userContextAvailable'] = 'true';
      } else {
        userContext['userContextAvailable'] = 'false';
      }
    } catch (userError) {
      userContext['userContextError'] = 'failed-to-extract';
      console.warn('Failed to extract user context for error:', userError);
    }

    return userContext;
  }

  /**
   * Log a handled exception to Application Insights
   * These are errors that the application caught and handled gracefully
   * @param error The error that was caught
   * @param severity The severity level of the error
   * @param context Additional context information about where/why the error occurred
   */
  logHandledException(
    error: Error,
    severity: 'info' | 'warning' | 'error' | 'critical' = 'warning',
    context?: Record<string, any>
  ): void {
    if (!this.ensureInitialized()) return;

    const userContext = this.extractUserContext();

    const properties = {
      severity,
      errorType: 'handled',
      timestamp: new Date().toISOString(),
      currentUrl: window.location.href,
      userAgent: navigator.userAgent,
      ...userContext,
      ...context,
    };

    this.trackException(error, properties);
  }

  /**
   * Log a handled error with just a message (when you don't have an Error object)
   * @param message The error message
   * @param severity The severity level
   * @param context Additional context information
   */
  logHandledError(
    message: string,
    severity: 'info' | 'warning' | 'error' | 'critical' = 'warning',
    context?: Record<string, any>
  ): void {
    if (!this.ensureInitialized()) return;

    const userContext = this.extractUserContext();

    const properties = {
      severity,
      errorType: 'handled',
      errorMessage: message,
      timestamp: new Date().toISOString(),
      currentUrl: window.location.href,
      userAgent: navigator.userAgent,
      ...userContext,
      ...context,
    };

    this.trackTrace(`Handled Error: ${message}`, {
      ...properties,
      level:
        severity === 'critical'
          ? 'Critical'
          : severity === 'error'
            ? 'Error'
            : severity === 'warning'
              ? 'Warning'
              : 'Information',
    });
  }

  /**
   * Log an HTTP error that was handled
   * @param url The URL that failed
   * @param statusCode The HTTP status code
   * @param method The HTTP method
   * @param errorMessage The error message
   * @param context Additional context
   */
  logHttpError(
    url: string,
    statusCode: number,
    method: string = 'GET',
    errorMessage?: string,
    context?: Record<string, any>
  ): void {
    const severity = statusCode >= 500 ? 'error' : statusCode >= 400 ? 'warning' : 'info';

    this.logHandledError(errorMessage || `HTTP ${statusCode} error`, severity, {
      httpUrl: url,
      httpStatusCode: statusCode.toString(),
      httpMethod: method,
      errorCategory: 'http',
      ...context,
    });
  }

  /**
   * Log a validation error that was handled
   * @param fieldName The field that failed validation
   * @param errorMessage The validation error message
   * @param context Additional context
   */
  logValidationError(fieldName: string, errorMessage: string, context?: Record<string, any>): void {
    this.logHandledError(`Validation error on ${fieldName}: ${errorMessage}`, 'info', {
      fieldName,
      errorCategory: 'validation',
      ...context,
    });
  }
}
